import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, moneyPrecise } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Trash, Clock, Copy, EnvelopeSimple, Lock, CheckCircle, Receipt, DownloadSimple, DotsThreeVertical, ShieldCheck, User, PaperPlaneTilt, Eye, Buildings, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Team() {
  const { user, subscription } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [history, setHistory] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [inviteRole, setInviteRole] = useState("member");
  const [projectsList, setProjectsList] = useState([]);
  const [scopeTarget, setScopeTarget] = useState(null);
  const [scopeSel, setScopeSel] = useState([]);
  const [emailLog, setEmailLog] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: "", email: "" });

  const loadEmailLog = () => api.get("/team/email-log").then((r) => setEmailLog(r.data)).catch(() => {});
  const loadContacts = () => api.get("/team/report-contacts").then((r) => setContacts(r.data)).catch(() => {});

  const addContact = async (e) => {
    e.preventDefault();
    if (!newContact.email.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      await api.post("/team/report-contacts", newContact);
      setNewContact({ name: "", email: "" });
      loadContacts();
      toast.success("Report contact saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save contact"); }
  };

  const removeContact = async (id) => {
    try {
      await api.delete(`/team/report-contacts/${id}`);
      loadContacts();
    } catch { toast.error("Could not remove contact"); }
  };

  const load = () => {
    api.get("/team/members").then((r) => setMembers(r.data));
    api.get("/team/invites").then((r) => setInvites(r.data));
    api.get("/team/settings").then((r) => setSettings(r.data));
    api.get("/billing/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjectsList(r.data)).catch(() => {});
    loadEmailLog();
    loadContacts();
  };
  useEffect(() => { load(); }, []);

  const openScope = (m) => { setScopeTarget(m); setScopeSel(m.viewer_project_ids || []); };
  const copyPortalLink = () => {
    const url = `${window.location.origin}/login`;
    navigator.clipboard?.writeText(url)
      .then(() => toast.success("Client portal link copied — share it with your client to sign in"))
      .catch(() => toast.message(url));
  };
  const toggleScope = (pid) => setScopeSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const saveScope = async () => {
    try {
      await api.put(`/team/members/${scopeTarget.id}/projects`, { project_ids: scopeSel });
      toast.success(scopeSel.length ? `Access limited to ${scopeSel.length} project(s)` : "Access set to all projects");
      setScopeTarget(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleClientDigest = async (m) => {
    const enabled = m.client_digest_off === true; // enabling if currently off
    try {
      await api.put(`/team/members/${m.id}/digest`, { enabled });
      toast.success(enabled ? "Weekly snapshot enabled for this client" : "Weekly snapshot paused for this client");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadReceipt = async (sessionId) => {
    try {
      const res = await api.get(`/billing/receipt/${sessionId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `buildtrack_receipt_${sessionId.slice(0, 12)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Could not download receipt"); }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSavingSettings(true);
    try {
      await api.put("/team/settings", next);
      toast.success("Digest settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const [sendingClient, setSendingClient] = useState(false);
  const sendClientDigest = async () => {
    setSendingClient(true);
    try {
      const { data: res } = await api.post("/reports/send-client-digest");
      if (res.sent > 0) toast.success(res.message);
      else toast.message(res.message || "No clients to send to — email delivery is limited to verified addresses in testing mode.");
    } catch (err) { toast.error("Could not send client snapshot"); }
    finally { setSendingClient(false); loadEmailLog(); }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const sendTeamDigest = async () => {
    setSendingDigest(true);
    try {
      const { data: res } = await api.post("/reports/send-digest");
      if (res.sent > 0) toast.success(res.message);
      else toast.message(res.message || "No recipients — email delivery is limited to verified addresses in testing mode.");
    } catch (err) { toast.error("Could not send team digest"); }
    finally { setSendingDigest(false); loadEmailLog(); }
  };

  const [digestPreview, setDigestPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const previewTeamDigest = async () => {
    setLoadingPreview(true);
    setDigestPreview({ html: "", recipient_count: null });
    try {
      const { data } = await api.get("/reports/preview-digest");
      setDigestPreview(data);
    } catch (err) { toast.error("Could not build preview"); setDigestPreview(null); }
    finally { setLoadingPreview(false); }
  };

  const [sendingPortfolio, setSendingPortfolio] = useState(false);
  const sendPortfolioReport = async () => {
    setSendingPortfolio(true);
    try {
      const { data: res } = await api.post("/reports/send-portfolio-report");
      if (res.sent > 0) toast.success(res.message);
      else toast.message(res.message || "No recipients — email delivery is limited to verified addresses in testing mode.");
    } catch (err) { toast.error("Could not send portfolio report"); }
    finally { setSendingPortfolio(false); loadEmailLog(); }
  };

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/team/invite", { email, role: inviteRole });
      if (data.invite_url) navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      if (data.email_sent) toast.success(`Invitation emailed to ${email} — link also copied to clipboard`);
      else {
        toast.success("Invite created — link copied to clipboard");
        toast.message("Email delivery isn't configured for external addresses. Share the copied link directly.");
      }
      setOpen(false);
      setEmail("");
      setInviteRole("member");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && typeof detail === "object" && detail.code === "seat_limit") {
        setOpen(false);
        setUpgrade({ max_users: detail.max_users });
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    }
    finally { setSaving(false); }
  };

  const NEXT_TIER = { 1: { id: "business", name: "Business", monthly: 59, annual: 599, users: "up to 3 users" }, 3: { id: "pro", name: "Pro", monthly: 99, annual: 1049, users: "up to 10 users" } };
  const doUpgrade = async (tier, interval) => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/billing/checkout", { tier, interval, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setUpgrading(false); }
  };

  const revoke = async (id) => { await api.delete(`/team/invites/${id}`); toast.success("Invite revoked"); load(); };

  const copyInviteLink = (invite) => {
    if (!invite.invite_url) { toast.error("Invite link unavailable — try resending"); return; }
    navigator.clipboard?.writeText(invite.invite_url)
      .then(() => toast.success("Invite link copied — share it directly with your teammate"))
      .catch(() => toast.message(invite.invite_url));
  };

  const resendInvite = async (invite) => {
    try {
      const { data } = await api.post("/team/invite", { email: invite.email });
      if (data.email_sent) toast.success(`Invitation re-sent to ${invite.email}`);
      else {
        navigator.clipboard?.writeText(data.invite_url).catch(() => {});
        toast.success("Invite link refreshed & copied to clipboard");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changeRole = async (member, role) => {
    if (member.role === role) return;
    try {
      await api.put(`/team/members/${member.id}/role`, { role });
      const roleLabel = { admin: "an admin", member: "a member", viewer: "Client Interphase (view-only)" }[role];
      toast.success(`${member.name || member.email} is now ${roleLabel}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/team/members/${removeTarget.id}`);
      toast.success(`${removeTarget.name || removeTarget.email} removed from the team`);
      setRemoveTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password updated");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div>
      <PageHeader label="Collaboration" title="Team">
        {isManager && (
          <Button onClick={() => setOpen(true)} className="rounded-none" data-testid="invite-member-button">
            <UserPlus size={16} className="mr-1" /> Invite member
          </Button>
        )}
      </PageHeader>

      <div className="p-4 sm:p-8 space-y-8">
        <div className="border border-border" data-testid="billing-section">
          <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
            {subscription?.active ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : <Lock size={16} />}
            <span className="font-heading font-bold">Billing & Subscription</span>
          </div>
          <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              {subscription?.active && subscription?.trial ? (
                <>
                  <p className="text-sm font-medium">Free trial <span className="text-amber-600 font-semibold">· {subscription.days_left} day{subscription.days_left === 1 ? "" : "s"} left</span></p>
                  <p className="text-xs text-muted-foreground mt-1">Full access during your trial. Subscribe to keep BuildTrack after {subscription.until ? new Date(subscription.until).toLocaleDateString() : "it ends"}.</p>
                </>
              ) : subscription?.active ? (
                <>
                  <p className="text-sm font-medium">BuildTrack <span className="capitalize">{subscription.tier_label || subscription.plan}</span> <span className="text-emerald-700 font-semibold">· Active</span></p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {subscription.member_count}/{subscription.max_users} seats used · valid until {subscription.until ? new Date(subscription.until).toLocaleDateString() : "—"}. Prepaid period — no card stored; renew or change tier anytime.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">You're in preview mode</p>
                  <p className="text-xs text-muted-foreground mt-1">Subscribe to unlock creating projects, cost tracking, AI extraction and your team.</p>
                </>
              )}
            </div>
            {isManager ? (
              <Button onClick={() => navigate("/pricing")} className="rounded-none shrink-0" data-testid="manage-billing-button">
                {subscription?.active && !subscription?.trial ? "Change / renew plan" : "View plans & subscribe"}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground shrink-0">Only the team owner can manage billing.</span>
            )}
          </div>
        </div>

        {isManager && history.length > 0 && (
          <div className="border border-border" data-testid="billing-history-section">
            <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
              <Receipt size={16} /><span className="font-heading font-bold">Billing History</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.session_id} data-testid={`billing-row-${h.session_id}`} className="hover:bg-secondary transition-colors duration-200">
                    <TableCell className="text-sm">{h.date ? new Date(h.date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-sm">
                      <span className="font-medium">{h.tier_label}</span>
                      <span className="text-muted-foreground capitalize"> · {h.interval}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums">{moneyPrecise(h.amount)}</TableCell>
                    <TableCell className="text-right">
                      <button onClick={() => downloadReceipt(h.session_id)} data-testid={`download-receipt-${h.session_id}`}
                        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                        <DownloadSimple size={16} /> Receipt
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="border border-border">
          <div className="px-4 py-3 border-b border-border bg-secondary"><span className="font-heading font-bold">Members</span></div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                {isManager && <TableHead className="text-right">Manage</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const canManage = isManager && m.role !== "owner" && m.id !== user?.id;
                return (
                  <TableRow key={m.id} data-testid={`member-row-${m.id}`} className="hover:bg-secondary transition-colors duration-200">
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.email}
                      {m.role === "viewer" && (
                        <span className="block text-xs mt-0.5" data-testid={`client-digest-status-${m.id}`}>
                          {m.client_digest_off
                            ? <span className="text-muted-foreground/70">Weekly snapshot paused</span>
                            : <span className="text-emerald-700">Weekly snapshot on{m.last_client_digest_at ? ` · last sent ${new Date(m.last_client_digest_at).toLocaleDateString()}` : ""}</span>}
                        </span>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="rounded-none capitalize" data-testid={`member-role-${m.id}`}>{m.role === "viewer" ? "Client Interphase" : m.role}</Badge></TableCell>
                    {isManager && (
                      <TableCell className="text-right">
                        {canManage ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`member-options-${m.id}`} aria-label="Member options"
                                className="inline-flex items-center justify-center h-8 w-8 text-muted-foreground hover:text-primary hover:bg-secondary transition-colors duration-200">
                                <DotsThreeVertical size={20} weight="bold" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-none w-48">
                              <DropdownMenuLabel className="label-caps text-xs">Manage member</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {m.role !== "admin" && (
                                <DropdownMenuItem onClick={() => changeRole(m, "admin")} data-testid={`make-admin-${m.id}`} className="cursor-pointer">
                                  <ShieldCheck size={16} className="mr-2" /> Make admin
                                </DropdownMenuItem>
                              )}
                              {m.role !== "member" && (
                                <DropdownMenuItem onClick={() => changeRole(m, "member")} data-testid={`make-member-${m.id}`} className="cursor-pointer">
                                  <User size={16} className="mr-2" /> Make member
                                </DropdownMenuItem>
                              )}
                              {m.role !== "viewer" && (
                                <DropdownMenuItem onClick={() => changeRole(m, "viewer")} data-testid={`make-viewer-${m.id}`} className="cursor-pointer">
                                  <Eye size={16} className="mr-2" /> Make Client Interphase
                                </DropdownMenuItem>
                              )}
                              {m.role === "viewer" && (
                                <DropdownMenuItem onClick={() => openScope(m)} data-testid={`scope-projects-${m.id}`} className="cursor-pointer">
                                  <Buildings size={16} className="mr-2" /> Manage project access
                                </DropdownMenuItem>
                              )}
                              {m.role === "viewer" && (
                                <DropdownMenuItem onClick={() => copyPortalLink()} data-testid={`copy-portal-${m.id}`} className="cursor-pointer">
                                  <Copy size={16} className="mr-2" /> Copy portal link
                                </DropdownMenuItem>
                              )}
                              {m.role === "viewer" && (
                                <DropdownMenuItem onClick={() => toggleClientDigest(m)} data-testid={`toggle-client-digest-${m.id}`} className="cursor-pointer">
                                  <EnvelopeSimple size={16} className="mr-2" /> {m.client_digest_off ? "Enable weekly snapshot" : "Pause weekly snapshot"}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setRemoveTarget(m)} data-testid={`remove-member-${m.id}`}
                                className="cursor-pointer text-destructive focus:text-destructive">
                                <Trash size={16} className="mr-2" /> Remove from team
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-xs text-muted-foreground">{m.role === "owner" ? "Owner" : "You"}</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="border border-border">
          <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
            <Clock size={16} /><span className="font-heading font-bold">Pending Invitations</span>
          </div>
          {invites.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">No pending invitations.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Email</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((i) => (
                  <TableRow key={i.id} data-testid={`invite-row-${i.id}`} className="hover:bg-secondary transition-colors duration-200">
                    <TableCell className="font-medium">{i.email}</TableCell>
                    <TableCell className="text-right">
                      {isManager && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`invite-options-${i.id}`} aria-label="Invitation options"
                              className="inline-flex items-center justify-center h-8 w-8 text-muted-foreground hover:text-primary hover:bg-secondary transition-colors duration-200">
                              <DotsThreeVertical size={20} weight="bold" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-none w-48">
                            <DropdownMenuLabel className="label-caps text-xs">Manage invite</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => resendInvite(i)} data-testid={`resend-invite-${i.id}`} className="cursor-pointer">
                              <PaperPlaneTilt size={16} className="mr-2" /> Resend invitation
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => copyInviteLink(i)} data-testid={`copy-invite-link-${i.id}`} className="cursor-pointer">
                              <Copy size={16} className="mr-2" /> Copy invite link
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => revoke(i.id)} data-testid={`revoke-invite-${i.id}`}
                              className="cursor-pointer text-destructive focus:text-destructive">
                              <Trash size={16} className="mr-2" /> Revoke invitation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {isManager && settings && (
          <div className="border border-border" data-testid="digest-settings">
            <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
              <EnvelopeSimple size={16} /><span className="font-heading font-bold">Weekly Digest</span>
            </div>
            <div className="p-6 space-y-5 max-w-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Email cost & activity summary</p>
                  <p className="text-xs text-muted-foreground">Automatically send a digest on a schedule.</p>
                </div>
                <Switch checked={settings.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })} data-testid="digest-enabled-switch" />
              </div>

              <div className="border-t border-border pt-5">
                <Label className="label-caps">Budget alert threshold</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">Flag a line item on the dashboard & digest once its actual reaches this % of budget (warns you before it's exceeded).</p>
                <div className="flex items-center gap-2">
                  <Input type="number" min="50" max="100" value={settings.alert_threshold_pct ?? 90}
                    onChange={(e) => setSettings({ ...settings, alert_threshold_pct: Number(e.target.value) })}
                    onBlur={(e) => saveSettings({ alert_threshold_pct: Math.min(100, Math.max(50, Number(e.target.value) || 90)) })}
                    data-testid="alert-threshold-input" className="rounded-none w-24 font-mono-fig" />
                  <span className="text-sm text-muted-foreground">% of budget</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-caps">Frequency</Label>
                  <Select value={settings.frequency} onValueChange={(v) => saveSettings({ frequency: v })} disabled={!settings.enabled}>
                    <SelectTrigger className="mt-2 rounded-none" data-testid="digest-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly (1st)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {settings.frequency === "weekly" && (
                  <div>
                    <Label className="label-caps">Day</Label>
                    <Select value={String(settings.day_of_week)} onValueChange={(v) => saveSettings({ day_of_week: Number(v) })} disabled={!settings.enabled}>
                      <SelectTrigger className="mt-2 rounded-none" data-testid="digest-day-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div>
                <Label className="label-caps">Recipients</Label>
                <Select value={settings.recipients} onValueChange={(v) => saveSettings({ recipients: v })} disabled={!settings.enabled}>
                  <SelectTrigger className="mt-2 rounded-none w-full" data-testid="digest-recipients-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owners">Owners & admins only</SelectItem>
                    <SelectItem value="all">All team members</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {savingSettings && <p className="text-xs text-muted-foreground">Saving…</p>}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={previewTeamDigest} disabled={loadingPreview} className="rounded-none" data-testid="preview-digest-button">
                  <Eye size={16} className="mr-2" /> {loadingPreview ? "Building…" : "Preview digest"}
                </Button>
                <Button variant="outline" size="sm" onClick={sendTeamDigest} disabled={sendingDigest} className="rounded-none" data-testid="send-digest-button">
                  <PaperPlaneTilt size={16} className="mr-2" /> {sendingDigest ? "Sending…" : "Send digest now"}
                </Button>
              </div>

              <div className="border-t border-border pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Client Interphase snapshot</p>
                    <p className="text-xs text-muted-foreground">Email each client (viewer) a scoped project snapshot on the same schedule.</p>
                  </div>
                  <Switch checked={!!settings.client_digest_enabled} onCheckedChange={(v) => saveSettings({ client_digest_enabled: v })} data-testid="client-digest-switch" />
                </div>
                <Button variant="outline" size="sm" onClick={sendClientDigest} disabled={sendingClient} className="rounded-none mt-3" data-testid="send-client-digest-button">
                  {sendingClient ? "Sending…" : "Send client snapshot now"}
                </Button>
              </div>

              <div className="border-t border-border pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Portfolio report (monthly)</p>
                    <p className="text-xs text-muted-foreground">Email owners/admins a multi-sheet Excel workbook (Summary + one tab per project with Cost Health) on your chosen day each month.</p>
                  </div>
                  <Switch checked={!!settings.portfolio_report_monthly} onCheckedChange={(v) => saveSettings({ portfolio_report_monthly: v })} data-testid="portfolio-report-switch" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="label-caps">Send on day of month</Label>
                    <Input type="number" min="1" max="28" value={settings.portfolio_report_day ?? 1}
                      disabled={!settings.portfolio_report_monthly}
                      onChange={(e) => setSettings({ ...settings, portfolio_report_day: Number(e.target.value) })}
                      onBlur={(e) => saveSettings({ portfolio_report_day: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })}
                      className="mt-2 rounded-none" data-testid="portfolio-report-day-input" />
                  </div>
                  <div>
                    <Label className="label-caps">CC (accountant / lender)</Label>
                    <Input type="text" value={settings.portfolio_report_cc ?? ""}
                      placeholder="email1@x.com, email2@y.com"
                      onChange={(e) => setSettings({ ...settings, portfolio_report_cc: e.target.value })}
                      onBlur={(e) => saveSettings({ portfolio_report_cc: e.target.value.trim() })}
                      className="mt-2 rounded-none" data-testid="portfolio-report-cc-input" />
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={sendPortfolioReport} disabled={sendingPortfolio} className="rounded-none mt-3" data-testid="send-portfolio-report-button">
                  {sendingPortfolio ? "Sending…" : "Send portfolio report now"}
                </Button>
              </div>

              <div className="border-t border-border pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Tax report (quarterly)</p>
                    <p className="text-xs text-muted-foreground">Email owners/admins the branded Taxes Paid PDF automatically on the 1st of Jan, Apr, Jul & Oct — handy for your accountant.</p>
                  </div>
                  <Switch checked={!!settings.tax_report_quarterly} onCheckedChange={(v) => saveSettings({ tax_report_quarterly: v })} data-testid="tax-report-switch" />
                </div>
                <div className="mt-4">
                  <Label className="label-caps">CC (accountant / bookkeeper)</Label>
                  <Input type="text" value={settings.tax_report_cc ?? ""}
                    placeholder="accountant@firm.com"
                    onChange={(e) => setSettings({ ...settings, tax_report_cc: e.target.value })}
                    onBlur={(e) => saveSettings({ tax_report_cc: e.target.value.trim() })}
                    className="mt-2 rounded-none max-w-md" data-testid="tax-report-cc-input" />
                </div>
              </div>

              <div className="border-t border-border pt-5" data-testid="report-contacts-section">
                <p className="text-sm font-medium mb-1">Report contacts</p>
                <p className="text-xs text-muted-foreground mb-3">Save clients, lenders or subcontractors here to quickly add them when emailing category cost reports.</p>
                <form onSubmit={addContact} className="flex flex-col sm:flex-row gap-2 mb-3">
                  <Input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    placeholder="Name (optional)" className="rounded-none sm:max-w-[180px]" data-testid="contact-name-input" />
                  <Input type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    placeholder="email@example.com" className="rounded-none flex-1" data-testid="contact-email-input" />
                  <Button type="submit" className="rounded-none" data-testid="contact-add-button">
                    <UserPlus size={14} className="mr-1" /> Add contact
                  </Button>
                </form>
                {contacts.length > 0 ? (
                  <div className="border border-border divide-y divide-border" data-testid="report-contacts-list">
                    {contacts.map((c) => (
                      <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`report-contact-${c.id}`}>
                        <span className="min-w-0 truncate">
                          {c.name && <span className="font-medium">{c.name} · </span>}
                          <span className="text-muted-foreground font-mono-fig">{c.email}</span>
                        </span>
                        <button onClick={() => removeContact(c.id)} data-testid={`report-contact-remove-${c.id}`}
                          className="text-muted-foreground hover:text-destructive transition-colors shrink-0" aria-label="Remove contact">
                          <Trash size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="report-contacts-empty">No saved contacts yet.</p>
                )}
              </div>

              {emailLog?.recent?.length > 0 && (
                <div className="border-t border-border pt-5" data-testid="email-log-section">
                  <p className="text-sm font-medium mb-1">Email delivery log</p>
                  <p className="text-xs text-muted-foreground mb-3">Most recent send per report type. Delivery is limited to verified addresses in testing mode.</p>
                  <div className="border border-border divide-y divide-border">
                    {[["team_digest", "Team digest"], ["client_digest", "Client snapshot"], ["portfolio_report", "Portfolio report"], ["category_report", "Category report"], ["tax_report", "Tax report"]]
                      .filter(([k]) => emailLog.last_by_kind?.[k])
                      .map(([k, label]) => {
                        const e = emailLog.last_by_kind[k];
                        const open = expandedLog === k;
                        return (
                          <div key={k} data-testid={`email-log-${k}`}>
                            <button onClick={() => setExpandedLog(open ? null : k)}
                              className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-secondary transition-colors duration-200"
                              data-testid={`email-log-toggle-${k}`}>
                              <span className="font-medium flex items-center gap-1.5">
                                <CaretRight size={12} className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
                                {label}
                              </span>
                              <span className="text-xs text-muted-foreground font-mono-fig">
                                {e.sent}/{e.total} recipient{e.total === 1 ? "" : "s"} · {new Date(e.created_at).toLocaleString()}
                              </span>
                            </button>
                            {open && (
                              <div className="px-3 pb-3 pl-8" data-testid={`email-log-recipients-${k}`}>
                                {e.recipients?.length ? (
                                  <ul className="text-xs text-muted-foreground space-y-0.5">
                                    {e.recipients.map((addr) => <li key={addr} className="font-mono-fig">{addr}</li>)}
                                  </ul>
                                ) : (
                                  <span className="text-xs text-muted-foreground">No recipients recorded.</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="border border-border" data-testid="change-password-section">
          <div className="px-4 py-3 border-b border-border bg-secondary flex items-center gap-2">
            <Lock size={16} /><span className="font-heading font-bold">Change Password</span>
          </div>
          <form onSubmit={changePassword} className="p-6 space-y-4 max-w-md" data-testid="change-password-form">
            <div>
              <Label className="label-caps">Current password</Label>
              <Input type="password" required value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                className="mt-2 rounded-none" data-testid="current-password-input" placeholder="Your current password" />
            </div>
            <div>
              <Label className="label-caps">New password</Label>
              <Input type="password" required minLength={6} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                className="mt-2 rounded-none" data-testid="new-password-input" placeholder="Min 6 characters" />
            </div>
            <div>
              <Label className="label-caps">Confirm new password</Label>
              <Input type="password" required minLength={6} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                className="mt-2 rounded-none" data-testid="confirm-password-input" placeholder="Re-enter new password" />
            </div>
            <Button type="submit" disabled={savingPwd} className="rounded-none" data-testid="change-password-button">
              {savingPwd ? "Updating…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading">Invite Team Member</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
            <p className="text-sm text-muted-foreground">
              They'll get an email invite to join your team and share all projects, vendors, and quotations.
            </p>
            <div>
              <Label className="label-caps">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" className="mt-2" placeholder="teammate@company.com" />
            </div>
            <div>
              <Label className="label-caps">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="rounded-none mt-2" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member — full access</SelectItem>
                  <SelectItem value="admin">Admin — manage team & billing</SelectItem>
                  <SelectItem value="viewer">Client Interphase — dashboard & reports only</SelectItem>
                </SelectContent>
              </Select>
              {inviteRole === "viewer" && (
                <p className="text-xs text-muted-foreground mt-2" data-testid="viewer-role-note">View-only access to the dashboard and cost reports. Unlimited — doesn't use a paid seat.</p>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="invite-send-button" className="rounded-none">{saving ? "Sending…" : "Send Invite"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Viewer project-access scope dialog */}
      <Dialog open={!!scopeTarget} onOpenChange={(o) => !o && setScopeTarget(null)}>
        <DialogContent className="rounded-none max-h-[85vh] overflow-y-auto" data-testid="scope-projects-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Project access</DialogTitle>
            <DialogDescription>
              Choose which projects {scopeTarget?.name || "this viewer"} can see. Select none to give access to all projects (whole portfolio).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 py-2">
            {projectsList.length === 0 ? (
              <p className="text-sm text-muted-foreground">No projects yet.</p>
            ) : projectsList.map((p) => (
              <label key={p.id} data-testid={`scope-project-${p.id}`} className="flex items-center gap-3 px-3 py-2 border border-border cursor-pointer hover:bg-secondary transition-colors duration-200">
                <input type="checkbox" checked={scopeSel.includes(p.id)} onChange={() => toggleScope(p.id)} className="h-4 w-4" />
                <span className="text-sm font-medium">{p.name}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">{scopeSel.length ? `${scopeSel.length} project(s) selected` : "All projects (no restriction)"}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScopeTarget(null)} className="rounded-none" data-testid="scope-cancel">Cancel</Button>
            <Button onClick={saveScope} className="rounded-none" data-testid="scope-save">Save access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove member confirmation */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="rounded-none" data-testid="remove-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Remove team member?</DialogTitle>
            <DialogDescription>
              {removeTarget && `${removeTarget.name || removeTarget.email} will lose access to this team's projects, vendors, and quotations. This can't be undone — they'd need a new invitation to rejoin.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} className="rounded-none" data-testid="remove-member-cancel">Cancel</Button>
            <Button variant="destructive" onClick={removeMember} className="rounded-none" data-testid="remove-member-confirm">Remove member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seat-limit upgrade modal */}
      <Dialog open={!!upgrade} onOpenChange={(o) => !o && setUpgrade(null)}>
        <DialogContent className="rounded-none" data-testid="upgrade-modal">
          <DialogHeader><DialogTitle className="font-heading">You've hit your seat limit</DialogTitle></DialogHeader>
          {upgrade && (() => {
            const next = NEXT_TIER[upgrade.max_users];
            if (!next) {
              return (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Your team is on the top plan ({upgrade.max_users} users). Need more seats? <button onClick={() => navigate("/pricing")} className="text-primary underline">See plans</button> or reach out.</p>
                </div>
              );
            }
            const annualSavings = Math.round(next.monthly * 12 - next.annual);
            const monthsFree = Math.round(annualSavings / next.monthly);
            return (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your current plan allows up to <strong>{upgrade.max_users}</strong> user{upgrade.max_users === 1 ? "" : "s"}. Upgrade to <strong>{next.name}</strong> ({next.users}) to invite more teammates.
                </p>
                <div className="border-2 border-[#0A0A0A] p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-heading font-black text-xl">{next.name}</div>
                      <div className="text-xs text-muted-foreground">{next.users}</div>
                    </div>
                    <div className="font-mono-fig text-2xl font-bold">${next.monthly}<span className="text-sm text-muted-foreground">/mo</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <Button onClick={() => doUpgrade(next.id, "monthly")} disabled={upgrading} className="rounded-none" data-testid="upgrade-monthly">
                      {upgrading ? "…" : `Monthly · $${next.monthly}`}
                    </Button>
                    <div className="relative">
                      {annualSavings > 0 && (
                        <span
                          data-testid="annual-savings-badge"
                          className="absolute -top-2 -right-1 z-10 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 leading-none shadow-sm"
                        >
                          Save ${annualSavings}
                        </span>
                      )}
                      <Button onClick={() => doUpgrade(next.id, "annual")} disabled={upgrading} variant="outline" className="rounded-none w-full" data-testid="upgrade-annual">
                        {upgrading ? "…" : `Annual · $${next.annual}`}
                      </Button>
                    </div>
                  </div>
                  {annualSavings > 0 && (
                    <p className="text-xs text-emerald-700 mt-3 font-medium" data-testid="annual-savings-note">
                      Go annual and save ${annualSavings}/yr{monthsFree >= 1 ? ` — that's ~${monthsFree} month${monthsFree === 1 ? "" : "s"} free` : ""}.
                    </p>
                  )}
                </div>
                <button onClick={() => { setUpgrade(null); navigate("/pricing"); }} className="text-sm text-muted-foreground hover:text-primary" data-testid="upgrade-see-all">Compare all plans →</button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!digestPreview} onOpenChange={(v) => !v && setDigestPreview(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden" data-testid="digest-preview-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="font-heading">Weekly digest preview</DialogTitle>
            <DialogDescription>
              {digestPreview?.recipient_count != null
                ? `This is exactly what will be emailed to ${digestPreview.recipient_count} recipient(s) on your schedule.`
                : "Rendering your digest…"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border-t border-border bg-muted/30 px-4 py-4">
            {loadingPreview || !digestPreview?.html ? (
              <p className="text-sm text-muted-foreground py-12 text-center" data-testid="digest-preview-loading">Building preview…</p>
            ) : (
              <iframe
                title="Digest preview"
                srcDoc={digestPreview.html}
                className="w-full bg-white border border-border"
                style={{ height: "60vh" }}
                data-testid="digest-preview-frame"
              />
            )}
          </div>
          <DialogFooter className="px-6 pb-6">
            <Button variant="outline" className="rounded-none" onClick={() => setDigestPreview(null)} data-testid="digest-preview-close">Close</Button>
            <Button className="rounded-none" onClick={() => { setDigestPreview(null); sendTeamDigest(); }} disabled={sendingDigest} data-testid="digest-preview-send">
              <PaperPlaneTilt size={16} className="mr-2" /> Send digest now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
